/**
 * Types and constants for the Funnel Visualizer.
 */

// ── Node Types ─────────────────────────────────────────

export type FunnelNodeType =
  | "ad"
  | "landing"
  | "checkout"
  | "upsell"
  | "downsell"
  | "thank_you"
  | "custom";

export interface FunnelNodeData {
  id: string;
  type: FunnelNodeType;
  label: string;
  /** Full page URL (from tracking data) */
  pageUrl?: string;
  /** Optional A/B variant label */
  variant?: "A" | "B";
  /** Incoming traffic (users) */
  trafficIn: number;
  /** Outgoing traffic (users going to next step) */
  trafficOut: number;
  /** Conversion rate to next step (0–100) */
  conversionRate: number;
  /** Optional revenue at this step */
  revenue?: number;
  /** Position on canvas */
  position: { x: number; y: number };
}

// ── Edge (connection between nodes) ────────────────────

export interface FunnelEdgeData {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  /** Users flowing through this edge */
  traffic: number;
  /** Is this the "yes" path (conversion) or "no" path (drop-off)? */
  edgeType: "conversion" | "dropoff";
}

// ── Funnel definition ──────────────────────────────────

export interface FunnelDefinition {
  id: string;
  name: string;
  nodes: FunnelNodeData[];
  edges: FunnelEdgeData[];
}

// ── Node type config ───────────────────────────────────

interface NodeTypeConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  iconName: string;
}

export const NODE_TYPE_CONFIG: Record<FunnelNodeType, NodeTypeConfig> = {
  ad: {
    label: "Reklama",
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    iconName: "megaphone",
  },
  landing: {
    label: "Landing Page",
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    iconName: "layout",
  },
  checkout: {
    label: "Checkout",
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    iconName: "creditCard",
  },
  upsell: {
    label: "Upsell",
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    iconName: "trendingUp",
  },
  downsell: {
    label: "Downsell",
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    iconName: "trendingDown",
  },
  thank_you: {
    label: "Thank You",
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    iconName: "checkCircle",
  },
  custom: {
    label: "Krok",
    color: "text-white/70",
    bgColor: "bg-white/5",
    borderColor: "border-white/20",
    iconName: "box",
  },
};

// ── Demo funnel data ───────────────────────────────────

export const DEMO_FUNNEL: FunnelDefinition = {
  id: "demo-ebook",
  name: "eBook — Funnel Pro",
  nodes: [
    {
      id: "ad-1",
      type: "ad",
      label: "Meta Ads — Zestaw 1",
      trafficIn: 1000,
      trafficOut: 320,
      conversionRate: 32,
      position: { x: 50, y: 180 },
    },
    {
      id: "ad-2",
      type: "ad",
      label: "Google Ads — Search",
      trafficIn: 600,
      trafficOut: 180,
      conversionRate: 30,
      position: { x: 50, y: 370 },
    },
    {
      id: "landing-1",
      type: "landing",
      label: "Landing Page",
      variant: "A",
      trafficIn: 500,
      trafficOut: 125,
      conversionRate: 25,
      position: { x: 320, y: 120 },
    },
    {
      id: "landing-2",
      type: "landing",
      label: "Landing Page",
      variant: "B",
      trafficIn: 500,
      trafficOut: 150,
      conversionRate: 30,
      position: { x: 320, y: 350 },
    },
    {
      id: "checkout-1",
      type: "checkout",
      label: "Checkout",
      trafficIn: 275,
      trafficOut: 138,
      conversionRate: 50,
      revenue: 13800,
      position: { x: 590, y: 220 },
    },
    {
      id: "upsell-1",
      type: "upsell",
      label: "Upsell — Kurs Video",
      trafficIn: 138,
      trafficOut: 41,
      conversionRate: 30,
      revenue: 8200,
      position: { x: 860, y: 140 },
    },
    {
      id: "downsell-1",
      type: "downsell",
      label: "Downsell — Mini Kurs",
      trafficIn: 97,
      trafficOut: 34,
      conversionRate: 35,
      revenue: 1700,
      position: { x: 860, y: 360 },
    },
    {
      id: "thankyou-1",
      type: "thank_you",
      label: "Dziękujemy!",
      trafficIn: 213,
      trafficOut: 213,
      conversionRate: 100,
      revenue: 23700,
      position: { x: 1130, y: 220 },
    },
  ],
  edges: [
    {
      id: "e-ad1-lp1",
      fromNodeId: "ad-1",
      toNodeId: "landing-1",
      traffic: 320,
      edgeType: "conversion",
    },
    {
      id: "e-ad2-lp2",
      fromNodeId: "ad-2",
      toNodeId: "landing-2",
      traffic: 180,
      edgeType: "conversion",
    },
    {
      id: "e-lp1-co",
      fromNodeId: "landing-1",
      toNodeId: "checkout-1",
      traffic: 125,
      edgeType: "conversion",
    },
    {
      id: "e-lp2-co",
      fromNodeId: "landing-2",
      toNodeId: "checkout-1",
      traffic: 150,
      edgeType: "conversion",
    },
    {
      id: "e-co-up",
      fromNodeId: "checkout-1",
      toNodeId: "upsell-1",
      traffic: 138,
      edgeType: "conversion",
    },
    {
      id: "e-up-ty",
      fromNodeId: "upsell-1",
      toNodeId: "thankyou-1",
      traffic: 41,
      edgeType: "conversion",
    },
    {
      id: "e-co-ds",
      fromNodeId: "checkout-1",
      toNodeId: "downsell-1",
      traffic: 97,
      edgeType: "dropoff",
    },
    {
      id: "e-ds-ty",
      fromNodeId: "downsell-1",
      toNodeId: "thankyou-1",
      traffic: 34,
      edgeType: "conversion",
    },
  ],
};
